import { labTestCatalogueByLoinc, labTestCodeKey, normalizeLabTestName, STRUCTURED_TEST_PATH_PREFIX, } from "./lab-test-catalogue.js";
/** Bound independently of form instruction enablement. Source revisions are pinned by the version test. */
export const LAB_TEST_MATCHING_VERSION = "guarded-display-v4";
/**
 * Existing reviewed-path display rules shared by the requisition renderer and editor. Catalogue
 * entries marked `codeOnly` in `LAB_TEST_CATALOGUE` have no pattern on purpose: a pattern would take
 * them out of the "Other tests" overflow on every form, including forms with no box for them.
 */
export const STRUCTURED_TEST_PATTERNS = {
    glucose: [/\b(?:fasting|random|serum|blood)?\s*glucose\b/i],
    hba1c: [/\bhba1c\b/i, /\bhemoglobin a1c\b/i, /\bglycated hemoglobin\b/i],
    creatinine_egfr: [/\bcreatinine\b/i, /\begfr\b/i],
    uric_acid: [/\buric acid\b/i],
    sodium: [/\bsodium\b/i],
    potassium: [/\bpotassium\b/i],
    alt: [/\balt\b/i, /\balanine aminotransferase\b/i],
    alkaline_phosphatase: [/\balkaline phosphatase\b/i, /\balk\.?\s*phos\b/i, /\balp\b/i],
    // A neonatal or newborn bilirubin is a different test from the adult total bilirubin box.
    bilirubin: [/(?<!\b(?:neonatal|newborn)\s+)\bbilirubin\b/i],
    albumin: [/\bserum albumin\b/i, /\balbumin level\b/i],
    // An explicit panel name remains a panel when its description lists individual constituents.
    lipid_assessment: [/\blipid (?:panel|profile|assessment)\b/i, /\bcholesterol panel\b/i],
    urine_albumin_creatinine_ratio: [
        /\balbumin\s*\/\s*creatinine ratio\b/i,
        /\burine albumin(?:-to-|\s+)creatinine ratio\b/i,
        /\buacr\b/i,
        /^albumin-to-creatinine ratio(?: \(acr\))?$/i,
        /^(?:urine )?acr$/i,
    ],
    urinalysis_chemical: [/\burinalysis\b/i, /\burine dip(?:stick)?\b/i],
    cbc: [/\bcbc\b/i, /\bcomplete blood count\b/i],
    inr: [/\b(?:pt\s*\/?\s*)?inr\b/i, /\bprothrombin time\b/i],
    pregnancy_test_urine: [
        /\burine pregnancy test\b/i,
        /\burine hcg\b/i,
        /^urine human chorionic gonadotropin(?: \(hcg\))?$/i,
    ],
    mononucleosis_screen: [/\bmononucleosis screen\b/i, /\bmonospot\b/i],
    rubella: [/\brubella\b/i],
    prenatal_abo_rhd_antibody_screen: [/\bprenatal\b.*\b(?:abo|rhd|antibody screen)\b/i],
    repeat_prenatal_antibodies: [/\brepeat prenatal antibod/i],
    "microbiology.cervical": [/\bcervical (?:culture|swab)\b/i],
    "microbiology.vaginal": [/\bvaginal (?:culture|swab)\b/i],
    group_b_strep: [/\bgroup b strep/i, /\bgbs (?:screen|culture|swab)\b/i],
    chlamydia: [/\bchlamydia\b/i],
    gonorrhea: [/\bgonorrh(?:ea|oea)\b/i, /\bneisseria gonorrhoeae\b/i],
    sputum_culture: [/\bsputum (?:culture|c&s)\b/i],
    throat_culture: [/\bthroat (?:culture|swab)\b/i],
    wound_culture: [/\bwound (?:culture|swab)\b/i],
    urine_culture: [
        /\burine culture\b/i,
        /^culture,\s*urine$/i,
        /^urine,?\s*(?:culture and sensitivity|c&s)$/i,
    ],
    stool_culture: [/\bstool culture\b/i],
    stool_ova_and_parasites: [/\bstool (?:ova|o&p).*\bparasite/i, /\bova and parasites\b/i],
    // hepatitis_b (anti-HBs) is code-only: free-text "Hepatitis B ..." wording names several
    // different tests, so it never ticks that box.
    hepatitis_a: [/\bhepatitis a\b/i],
    hepatitis_c: [/\bhepatitis c\b/i],
    total_psa: [/\btotal psa\b/i, /\bprostate specific antigen\b/i],
    free_psa: [/\bfree psa\b/i],
};
export function matchesStructuredTest(testPath, value) {
    const name = normalizeLabTestName(value);
    // Qualifiers are specific to an assay: differential is accepted by the existing CBC
    // display mapping, and serology is ordinary wording for rubella/hepatitis. Neither is a
    // global reason to refuse a reviewed box. Distinct assays retain their full write-in name.
    if (STRUCTURED_TEST_EXCLUSIONS[testPath]?.test(name))
        return false;
    const urine = /\burin(?:e|ary)\b/i.test(name);
    const blood = /\b(?:blood|serum|plasma|venous)\b/i.test(name);
    if ([
        "pregnancy_test_urine",
        "urine_culture",
        "urine_albumin_creatinine_ratio",
        "urinalysis_chemical",
    ].includes(testPath) &&
        blood)
        return false;
    if (["glucose", "hba1c", "creatinine_egfr", "albumin", "sodium", "potassium"].includes(testPath) &&
        urine)
        return false;
    if (testPath === "creatinine_egfr" && /\balbumin\b|\bacr\b|\buacr\b/i.test(name))
        return false;
    if (["chlamydia", "gonorrhea"].includes(testPath) && blood)
        return false;
    return (STRUCTURED_TEST_PATTERNS[testPath] ?? []).some((pattern) => pattern.test(name));
}
/** Qualifiers that change the assay represented by a particular reviewed path. */
const STRUCTURED_TEST_EXCLUSIONS = {
    glucose: /\b(?:rapid|point.of.care|capillary|fingerstick)\b/i,
    hba1c: /\b(?:rapid|point.of.care|capillary|fingerstick)\b/i,
    cbc: /\b(?:reflex|manual|rapid|point.of.care)\b/i,
    bilirubin: /\b(?:neonatal|newborn|direct|conjugated)\b/i,
    chlamydia: /\b(?:pneumoniae|serology|rapid|point.of.care)\b/i,
    gonorrhea: /\b(?:serology|rapid|point.of.care)\b/i,
    rubella: /\b(?:viral load|rna|pcr)\b/i,
    hepatitis_a: /\b(?:viral load|rna|pcr|reflex)\b/i,
    hepatitis_c: /\b(?:viral load|rna|pcr|reflex)\b/i,
    urine_culture: /\breflex\b/i,
};
/**
 * Whether a test is one the requisition's own boxes carry, and so never prints on an "Other tests"
 * line, on any form: the same decision the renderer makes for each ordered test. A catalogue-coded
 * test is decided by its own code (a box test exactly when its catalogue key has a box pattern);
 * any other test by the reviewed display rules.
 */
export function isRequisitionBoxTest(test) {
    const ownKey = test.codeSystem.trim().toUpperCase() === "LOINC"
        ? labTestCatalogueByLoinc.get(test.code.trim())?.key
        : undefined;
    if (ownKey !== undefined)
        return Object.hasOwn(STRUCTURED_TEST_PATTERNS, ownKey);
    return Object.keys(STRUCTURED_TEST_PATTERNS).some((testPath) => matchesStructuredTest(testPath, test.display));
}
/**
 * Exact identities match regardless of display. Only an explicitly free-text
 * request may use the existing display rules for a reviewed structured test
 * path; a conflicting LOINC/local identity never falls back to its label.
 */
export function labOrderTestMatchesBinding(test, binding) {
    if (binding.code !== undefined &&
        binding.codeSystem !== undefined &&
        labTestCodeKey(test.codeSystem, test.code) === labTestCodeKey(binding.codeSystem, binding.code)) {
        return true;
    }
    const path = binding.sourcePath;
    return (test.codeSystem.trim().toUpperCase() === "FREE_TEXT" &&
        path !== undefined &&
        path.startsWith(STRUCTURED_TEST_PATH_PREFIX) &&
        matchesStructuredTest(path.slice(STRUCTURED_TEST_PATH_PREFIX.length), test.display));
}
//# sourceMappingURL=lab-test-matching.js.map